import React, { useState } from "react";
import { ExternalLink, Search } from "lucide-react";
import { useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { Doc } from "../../../convex/_generated/dataModel";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

type Job = Doc<"jobs">;

export default function JobBoard() {
  const [filterType, setFilterType] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  
  const jobs = useQuery(api.jobs.list, { 
    type: filterType === "all" ? undefined : filterType,
    searchQuery: searchQuery || undefined 
  });

  const isLoading = jobs === undefined;
  const filteredJobs: Job[] = jobs ?? [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-sm text-muted-foreground">Loading jobs...</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Dense Toolbar */}
      <div className="border-b border-border p-2 flex items-center gap-4 bg-card">
        <div className="relative w-64">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <input 
            placeholder="Filter..." 
            className="w-full h-8 pl-8 pr-3 bg-background border border-border rounded-sm text-sm focus:outline-none focus:border-primary placeholder:text-muted-foreground"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        
        <div className="flex items-center border border-border rounded-sm overflow-hidden h-8">
          <button 
            onClick={() => setFilterType("all")}
            className={cn(
              "px-3 text-xs font-medium h-full border-r border-border transition-colors",
              filterType === "all" ? "bg-secondary text-foreground" : "hover:bg-secondary/50"
            )}
          >
            All
          </button>
          <button 
            onClick={() => setFilterType("hourly")}
            className={cn(
              "px-3 text-xs font-medium h-full border-r border-border transition-colors",
              filterType === "hourly" ? "bg-secondary text-foreground" : "hover:bg-secondary/50"
            )}
          >
            Hourly
          </button>
          <button 
            onClick={() => setFilterType("ote")}
            className={cn(
              "px-3 text-xs font-medium h-full transition-colors",
              filterType === "ote" ? "bg-secondary text-foreground" : "hover:bg-secondary/50"
            )}
          >
            OTE
          </button>
        </div>

        <div className="ml-auto text-xs font-mono text-muted-foreground">
          {filteredJobs.length} Records
        </div>
      </div>

      {/* Data Grid */}
      <div className="flex-1 overflow-auto">
        <Table>
          <TableHeader className="bg-secondary/30 sticky top-0 z-10">
            <TableRow className="hover:bg-transparent border-b border-border">
              <TableHead className="w-[30px]"></TableHead>
              <TableHead className="h-9 text-xs font-medium text-muted-foreground">Posted</TableHead>
              <TableHead className="h-9 text-xs font-medium text-muted-foreground">Role / Company</TableHead>
              <TableHead className="h-9 text-xs font-medium text-muted-foreground">Rate</TableHead>
              <TableHead className="h-9 text-xs font-medium text-muted-foreground">Type</TableHead>
              <TableHead className="h-9 text-xs font-medium text-muted-foreground">Source</TableHead>
              <TableHead className="h-9 text-xs font-medium text-muted-foreground w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredJobs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="h-64 text-center">
                  <div className="flex flex-col items-center justify-center text-muted-foreground">
                    <p className="text-sm mb-1">No jobs found</p>
                    <p className="text-xs">Configure sources in Scraper Status to start collecting jobs</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              filteredJobs.map((job) => (
                <TableRow key={job._id} className="group hover:bg-secondary/20 border-b border-border/50 data-[new=true]:bg-blue-500/5" data-new={job.isNew}>
                  <TableCell className="py-2 px-4 align-top">
                    {job.isNew && (
                      <div className="w-2 h-2 rounded-full bg-blue-500 mt-1.5" title="New" />
                    )}
                  </TableCell>
                  <TableCell className="py-2 align-top text-xs font-mono text-muted-foreground whitespace-nowrap">
                    {new Date(job.postedAt).toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' })}
                    <span className="block text-[10px] opacity-70">{new Date(job.postedAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                  </TableCell>
                  <TableCell className="py-2 align-top">
                    <div className="font-medium text-sm text-foreground group-hover:underline decoration-dotted cursor-pointer">
                      {job.title}
                    </div>
                    <div className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
                      <span className="font-semibold">{job.company}</span>
                      <span>•</span>
                      <span>{job.location}</span>
                      {job.rating > 0 && (
                        <>
                          <span>•</span>
                          <span className="text-amber-600 dark:text-amber-500 font-medium">{job.rating}★</span>
                        </>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {job.tags.map(tag => (
                        <span key={tag} className="px-1 py-0 rounded-sm bg-secondary text-[10px] text-secondary-foreground border border-border/50">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="py-2 align-top font-mono text-xs font-medium">
                    {job.rate}
                  </TableCell>
                  <TableCell className="py-2 align-top text-xs">
                    {job.type}
                  </TableCell>
                  <TableCell className="py-2 align-top text-xs text-muted-foreground">
                    {job.source}
                  </TableCell>
                  <TableCell className="py-2 align-top text-right">
                    {job.url ? (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        asChild
                      >
                        <a href={job.url} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        disabled
                        title="No URL available"
                      >
                        <ExternalLink className="w-3 h-3 opacity-30" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
